import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Card,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "../../utils/axiosConfig";
import { fadeInUp, fadeInDown } from "../../utils/animations";

function Newsletter() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/newsletter/");
      setSubscribers(Array.isArray(res.data) ? res.data : []);
    } catch {
      setSnackbar({ open: true, message: "Erreur de chargement des abonnés.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const handleUnsubscribe = useCallback(
    (email) => {
      setConfirmDialog({
        open: true,
        message: `Désabonner ${email} de la newsletter ?`,
        onConfirm: async () => {
          try {
            await api.delete(`/api/newsletter/${encodeURIComponent(email)}`);
            setSnackbar({
              open: true,
              message: "Abonné désactivé avec succès.",
              severity: "success",
            });
            fetchSubscribers();
          } catch {
            setSnackbar({
              open: true,
              message: "Erreur lors de la désactivation.",
              severity: "error",
            });
          }
        },
      });
    },
    [fetchSubscribers]
  );

  const ActionCell = ({ row }) => (
    <Tooltip title="Désabonner">
      <IconButton size="small" onClick={() => handleUnsubscribe(row.original.email)}>
        <Icon sx={{ fontSize: 18, color: "error.main" }}>person_remove</Icon>
      </IconButton>
    </Tooltip>
  );
  ActionCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({ email: PropTypes.string.isRequired }).isRequired,
    }).isRequired,
  };

  const StatutCell = ({ value }) => (
    <Chip
      label={value ? "Actif" : "Inactif"}
      size="small"
      color={value ? "success" : "default"}
      sx={{ fontWeight: "bold", fontSize: "0.7rem" }}
    />
  );
  StatutCell.propTypes = { value: PropTypes.bool.isRequired };

  const DateCell = ({ value }) => (
    <MDTypography variant="caption" color="text">
      {value
        ? new Date(value).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
        : "-"}
    </MDTypography>
  );
  DateCell.propTypes = { value: PropTypes.string };

  const columns = [
    { Header: "#", accessor: "id", width: "8%" },
    { Header: "Email", accessor: "email", wrap: true },
    { Header: "Statut", accessor: "actif", align: "center", Cell: StatutCell },
    { Header: "Date d'inscription", accessor: "date_inscription", Cell: DateCell },
    { Header: "Actions", accessor: "actions", align: "center", Cell: ActionCell },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="purple"
                borderRadius="lg"
                coloredShadow="dark"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
              >
                <MDBox>
                  <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
                    Abonnés Newsletter
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                    {subscribers.length} abonné{subscribers.length !== 1 ? "s" : ""} actif
                    {subscribers.length !== 1 ? "s" : ""}
                  </MDTypography>
                </MDBox>
                <MDButton variant="outlined" color="white" size="small" onClick={fetchSubscribers}>
                  <Icon sx={{ mr: 0.5 }}>refresh</Icon>
                  Actualiser
                </MDButton>
              </MDBox>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : subscribers.length === 0 ? (
                  <MDBox p={5} textAlign="center">
                    <Icon sx={{ fontSize: "48px", color: "grey.400", mb: 1 }}>mail_outline</Icon>
                    <MDTypography variant="h6" color="text">
                      Aucun abonné pour le moment
                    </MDTypography>
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                      Les utilisateurs peuvent s&apos;inscrire depuis la page d&apos;accueil.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows: subscribers }}
                    isSorted
                    entriesPerPage
                    showTotalEntries
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="secondary"
          >
            Annuler
          </MDButton>
          <MDButton
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
            variant="gradient"
            color="warning"
          >
            Confirmer
          </MDButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default Newsletter;
